/**
 * ConsentBanner — GA4 consent gate for shariqrefai.com.
 *
 * Strategy: Google Consent Mode v2 with strict, deny-by-default gating.
 *
 * The gtag stub, dataLayer, and `gtag('consent', 'default', denied)` are seeded
 * in index.html so any queued events stay cookieless. This component is the
 * only place that ever fetches the actual gtag.js loader, and only after
 * explicit user consent.
 *
 * Signals honored automatically (no banner shown):
 *   - navigator.webdriver          — prerender/crawlers
 *   - navigator.userAgent HeadlessChrome — same
 *   - navigator.globalPrivacyControl === true — CPRA opt-out signal
 *   - navigator.doNotTrack === "1" — legacy DNT
 *
 * Persistence: localStorage key `consent_v1` with values "granted" | "denied".
 * Version the key when the disclosures materially change so users are
 * re-prompted.
 *
 * Revocation: /cookies calls `resetConsent()` and reloads.
 */

import { useEffect, useState } from "react";
import { Link } from "wouter";

const STORAGE_KEY = "consent_v1";
const GA_ID = "G-P568VWDRRY";

type Choice = "granted" | "denied";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    __ga_loaded?: boolean;
  }
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}

function isBotOrPrerender(): boolean {
  if (typeof navigator === "undefined") return true;
  if (navigator.webdriver) return true;
  const ua = (navigator.userAgent || "").toLowerCase();
  return (
    ua.includes("headlesschrome") ||
    ua.includes("puppeteer") ||
    ua.includes("googlebot") ||
    ua.includes("bingbot") ||
    ua.includes("slackbot")
  );
}

function autoOptedOut(): boolean {
  if (typeof navigator === "undefined") return false;
  if (navigator.globalPrivacyControl === true) return true;
  if (navigator.doNotTrack === "1") return true;
  return false;
}

function readChoice(): Choice | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "granted" || v === "denied") return v;
    return null;
  } catch {
    return null;
  }
}

function writeChoice(v: Choice): void {
  try {
    localStorage.setItem(STORAGE_KEY, v);
  } catch {
    // Storage may be blocked (Safari private, quota). Silent fail is fine;
    // the banner will just reappear next visit.
  }
}

/** Load the actual GA4 loader script and mark it loaded. */
function loadGtagScript(): void {
  if (typeof document === "undefined") return;
  if (window.__ga_loaded) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.__ga_loaded = true;
}

function grantConsent(): void {
  writeChoice("granted");
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }
  loadGtagScript();
}

function denyConsent(): void {
  writeChoice("denied");
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }
}

/** Clear the stored decision so the banner reappears. Used by /cookies. */
export function resetConsent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }
}

export function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Never show or act for prerender, bots, or auto-opted-out signals.
    if (isBotOrPrerender()) return;

    if (autoOptedOut()) {
      // Respect GPC/DNT silently. Persist so the banner never shows.
      writeChoice("denied");
      return;
    }

    const choice = readChoice();
    if (choice === "granted") {
      // Returning consented user: load the loader now.
      grantConsent();
      return;
    }
    if (choice === "denied") {
      // Already refused. Stay quiet.
      return;
    }
    // Undecided: show the banner.
    setVisible(true);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 md:px-6 md:pb-6"
    >
      <div className="mx-auto max-w-4xl rounded-lg border border-border bg-background shadow-2xl">
        <div className="flex flex-col gap-4 p-5 md:p-6 md:flex-row md:items-start md:justify-between">
          <div className="flex-1">
            <p className="font-serif text-lg text-primary leading-snug">
              A quick note on analytics.
            </p>
            <p className="mt-2 text-sm text-secondary font-light leading-relaxed">
              This site uses Google Analytics to measure aggregate traffic, so
              I can see which essays are actually being read. No advertising
              cookies, no profiling, and nothing tied to health information.
              You can accept, decline, or read the details in the{" "}
              <Link
                href="/cookies"
                className="text-accent underline underline-offset-2 hover:text-primary"
              >
                Cookies policy
              </Link>
              .
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row md:flex-col md:min-w-[160px]">
            <button
              type="button"
              onClick={() => {
                grantConsent();
                setVisible(false);
              }}
              className="inline-flex h-10 items-center justify-center bg-primary px-5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              Accept analytics
            </button>
            <button
              type="button"
              onClick={() => {
                denyConsent();
                setVisible(false);
              }}
              className="inline-flex h-10 items-center justify-center border border-border bg-background px-5 text-sm font-medium text-secondary transition-colors hover:border-primary hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
